import React, { useState } from 'react';
import AutoExpandingTextInput from 'components/textinput/textinput';
import LookupField from './LookupField';
import ListField from './ListField';
import styles from './SchemaFieldRenderer.module.css';

function SchemaFieldRenderer({ 
  fieldSchema, 
  fieldName, 
  value, 
  onChange, 
  onBlur,
  onKeyDown,
  isEditing, 
  lookupData = {},
  className = '',
  autoFocus = false
}) {
  const [localValue, setLocalValue] = useState(value);

  // Handle local value changes
  const handleLocalChange = (newValue) => {
    setLocalValue(newValue);
    if (onChange) onChange(newValue);
  };

  const renderReadOnlyField = () => {
    switch (fieldSchema.type) {
      case 'string':
        if (!value) return <span className={styles.nullValue}>N/A</span>;
        return (
          <span className={styles.stringValue}>
            {String(value).split('\n').map((line, idx, arr) => (
              <span key={idx}>
                {line}
                {idx < arr.length - 1 && <br />}
              </span>
            ))}
          </span>
        );

      case 'number':
        if (value === null || value === undefined) {
          return <span className={styles.nullValue}>N/A</span>;
        }
        return (
          <span className={styles.numberValue}>
            {fieldName.includes('amount') || fieldName.includes('balance') 
              ? Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
              : Number(value).toLocaleString()
            }
          </span>
        );

      case 'date':
      case 'datetime':
        if (!value) return <span className={styles.nullValue}>N/A</span>;
        const date = new Date(value);
        return (
          <span className={styles.dateValue}>
            {fieldSchema.type === 'datetime' 
              ? date.toLocaleString()
              : date.toLocaleDateString()
            }
          </span>
        );

      case 'boolean':
        return (
          <span className={`${styles.booleanValue} ${value ? styles.booleanTrue : styles.booleanFalse}`}>
            {value ? 'Yes' : 'No'}
          </span>
        );

      case 'lookup':
        return (
          <LookupField 
            fieldSchema={fieldSchema}
            value={value}
            isEditing={false}
            lookupData={lookupData[fieldSchema.lookup]}
          />
        );

      case 'list':
        return (
          <ListField 
            fieldSchema={fieldSchema}
            value={value}
            isEditing={false}
            lookupData={lookupData}
          />
        );

      default:
        if (value === null || value === undefined) {
          return <span className={styles.nullValue}>N/A</span>;
        }
        return (
          <span className={styles.unknownValue}>
            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
          </span>
        );
    }
  };

  const renderEditableField = () => {
    switch (fieldSchema.type) {
      case 'string':
        return (
          <AutoExpandingTextInput
            value={localValue || ''}
            onChange={(e) => handleLocalChange(e.target.value)}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            className={`${styles.editableInput} ${className}`}
            autoFocus={autoFocus}
            placeholder={`Enter ${fieldName.replace(/_/g, ' ')}`}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={localValue || 0}
            onChange={(e) => handleLocalChange(Number(e.target.value))}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            className={`${styles.editableInput} ${className}`}
            autoFocus={autoFocus}
            step={fieldSchema.step || 0.01}
            placeholder="0"
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={localValue ? new Date(localValue).toISOString().split('T')[0] : ''}
            onChange={(e) => handleLocalChange(e.target.value)}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            className={`${styles.editableInput} ${className}`}
            autoFocus={autoFocus}
          />
        );

      case 'datetime':
        return (
          <input
            type="datetime-local"
            value={localValue ? new Date(localValue).toISOString().slice(0, 16) : ''}
            onChange={(e) => handleLocalChange(e.target.value)}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            className={`${styles.editableInput} ${className}`}
            autoFocus={autoFocus}
          />
        );

      case 'boolean':
        return (
          <input
            type="checkbox"
            checked={localValue || false}
            onChange={(e) => handleLocalChange(e.target.checked)}
            onBlur={onBlur}
            className={`${styles.editableCheckbox} ${className}`}
            autoFocus={autoFocus}
          />
        );

      case 'lookup':
        return (
          <LookupField 
            fieldSchema={fieldSchema}
            value={localValue}
            onChange={handleLocalChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            isEditing={true}
            lookupData={lookupData[fieldSchema.lookup]}
            className={className}
            autoFocus={autoFocus}
          />
        );

      case 'list':
        return (
          <ListField 
            fieldSchema={fieldSchema}
            value={localValue}
            onChange={handleLocalChange}
            onBlur={onBlur}
            isEditing={true}
            lookupData={lookupData}
            className={className}
          />
        );

      default:
        return (
          <input
            type="text"
            value={localValue || ''}
            onChange={(e) => handleLocalChange(e.target.value)}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            className={`${styles.editableInput} ${className}`}
            autoFocus={autoFocus}
            placeholder={`Enter ${fieldName.replace(/_/g, ' ')}`}
          />
        );
    }
  };

  const renderFieldWithLabel = (content) => {
    return (
      <div className={styles.fieldContainer}>
        {content}
      </div>
    );
  };

  // Render click-to-edit wrapper for read-only fields
  const renderClickToEdit = (content) => {
    if (!fieldSchema.editable) return content;
    
    return (
      <div 
        className={styles.clickToEdit}
        onClick={() => isEditing ? null : onChange?.(value)}
        title={isEditing ? '' : 'Click to edit'}
      >
        {content}
      </div>
    );
  };

  if (isEditing && fieldSchema.editable) {
    return renderFieldWithLabel(renderEditableField());
  }

  return renderFieldWithLabel(renderClickToEdit(renderReadOnlyField()));
}

export default SchemaFieldRenderer;